/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingList,
  FloatingNode,
  FloatingPortal,
  FloatingTree,
  offset,
  safePolygon,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useFloatingNodeId,
  useFloatingParentNodeId,
  useFloatingTree,
  useHover,
  useInteractions,
  useListItem,
  useListNavigation,
  useMergeRefs,
  useRole,
  useTypeahead,
} from '@floating-ui/react';
import classNames from 'classnames';
import type React from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  type CommonProps,
  type PolymorphicCommonProps,
} from '../_common/types';
import { ConditionalWrap } from '../conditional-wrap';
import { useIsInsideDialog } from '../dialog/dialog-context';
import { Divider } from '../divider';
import { CheckIconOutline, ChevronRightIconOutline } from '../icons';
import { placementTranslation } from '../popover';
import { useNeedleTheme } from '../theme';
import type {
  CategoryItemProps,
  MenuGroupProps,
  MenuItemComponentProps,
  MenuItemProps,
  MenuItemsProps,
  MenuProps,
  NestedMenuItemProps,
  RadioItemProps,
} from './menu-types';

const MenuContext = createContext<{
  getItemProps: (
    userProps?: React.HTMLProps<HTMLElement>,
  ) => Record<string, unknown>;
  activeIndex: number | null;
  setActiveIndex: React.Dispatch<React.SetStateAction<number | null>>;
  setHasFocusInside: React.Dispatch<React.SetStateAction<boolean>>;
  isOpen: boolean;
}>({
  activeIndex: null,
  getItemProps: () => ({}),
  isOpen: false,
  // oxlint-disable-next-line @typescript-eslint/no-empty-function
  setActiveIndex: () => {},
  // oxlint-disable-next-line @typescript-eslint/no-empty-function
  setHasFocusInside: () => {},
});

const MenuWrapper = (props: React.ComponentProps<typeof MenuComponent>) => {
  const parentId = useFloatingParentNodeId();

  if (parentId === null) {
    return (
      <FloatingTree>
        <MenuComponent {...props} isRoot />
      </FloatingTree>
    );
  }

  return <MenuComponent {...props} />;
};

const MenuComponent = <T extends React.ElementType = 'div'>({
  children,
  isOpen: controlledOpen,
  onClose,
  isRoot,
  anchorRef,
  as,
  className,
  placement,
  minWidth,
  title,
  isDisabled,
  description,
  icon,
  isPortaled = true,
  portalTarget,
  htmlAttributes,
  strategy,
  ref: forwardedRef,
  style,
}: PolymorphicCommonProps<T, MenuProps>) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasFocusInside, setHasFocusInside] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const elementsRef = useRef<Array<HTMLButtonElement | null>>([]);
  const labelsRef = useRef<Array<string | null>>([]);
  const parent = useContext(MenuContext);

  const isInsideDialog = useIsInsideDialog();
  const tree = useFloatingTree();
  const nodeId = useFloatingNodeId();
  const parentId = useFloatingParentNodeId();
  const item = useListItem();
  const { themeClassName } = useNeedleTheme();
  useEffect(() => {
    if (controlledOpen === undefined) {
      return;
    }
    setIsOpen(controlledOpen);
  }, [controlledOpen]);

  useEffect(() => {
    if (isOpen) {
      setActiveIndex(0);
    }
  }, [isOpen]);

  const Component = as ?? 'div';

  const isNested = parentId !== null;

  const defaultPlacement = isNested ? 'right-start' : 'bottom-start';

  const { floatingStyles, refs, context } = useFloating<HTMLButtonElement>({
    elements: {
      reference: anchorRef?.current,
    },
    middleware: [
      offset({
        alignmentAxis: isNested ? -4 : 0,
        mainAxis: isNested ? 0 : 4,
      }),
      ...(isNested ? [shift()] : []),
      flip(
        isNested
          ? {
              fallbackPlacements: ['left-start', 'bottom-start', 'top-start'],
              fallbackStrategy: 'bestFit',
            }
          : { fallbackPlacements: ['left-start', 'right-start'] },
      ),
      shift(),
    ],
    nodeId,
    onOpenChange: (open, event) => {
      if (controlledOpen === undefined) {
        setIsOpen(open);
      }

      if (!open) {
        if (event instanceof PointerEvent) {
          onClose?.(event, { type: 'backdropClick' });
        } else if (event instanceof KeyboardEvent) {
          onClose?.(event, { type: 'escapeKeyDown' });
        } else if (event instanceof FocusEvent) {
          onClose?.(event, { type: 'focusOut' });
        }
      }
    },
    open: isOpen,
    placement: placement ? placementTranslation[placement] : defaultPlacement,
    strategy: strategy ?? (isInsideDialog ? 'fixed' : 'absolute'),
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context, {
    delay: { open: 75 },
    enabled: isNested,
    handleClose: safePolygon({ blockPointerEvents: true }),
  });
  const click = useClick(context, {
    event: 'mousedown',
    ignoreMouse: isNested,
    toggle: !isNested,
  });
  const role = useRole(context, { role: 'menu' });
  const dismiss = useDismiss(context, { bubbles: true });
  const listNavigation = useListNavigation(context, {
    activeIndex,
    listRef: elementsRef,
    nested: isNested,
    onNavigate: setActiveIndex,
  });
  const typeahead = useTypeahead(context, {
    activeIndex,
    listRef: labelsRef,
    onMatch: isOpen ? setActiveIndex : undefined,
  });

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions(
    [hover, click, role, dismiss, listNavigation, typeahead],
  );

  // Event emitter allows you to communicate across tree components.
  // This effect closes all menus when an item gets clicked anywhere
  // in the tree.
  useEffect(() => {
    if (!tree) {
      return;
    }

    function handleTreeClick(data: { id?: string }) {
      if (controlledOpen === undefined) {
        setIsOpen(false);
      }
      onClose?.(undefined, { id: data?.id, type: 'itemClick' });
    }

    function onSubMenuOpen(event: { nodeId: string; parentId: string }) {
      if (event.nodeId !== nodeId && event.parentId === parentId) {
        if (controlledOpen === undefined) {
          setIsOpen(false);
        }
        onClose?.(undefined, { type: 'itemClick' });
      }
    }

    tree.events.on('click', handleTreeClick);
    tree.events.on('menuopen', onSubMenuOpen);

    return () => {
      tree.events.off('click', handleTreeClick);
      tree.events.off('menuopen', onSubMenuOpen);
    };
  }, [tree, nodeId, parentId, onClose, controlledOpen]);

  useEffect(() => {
    if (isOpen && tree) {
      tree.events.emit('menuopen', { nodeId, parentId });
    }
  }, [tree, isOpen, nodeId, parentId]);

  // Workaround for floating-ui bug where Shift+Tab doesn't trigger
  // closeOnFocusOut.
  // We detect Shift+Tab and check after guards redirect focus whether
  // focus has actually left the floating element.
  // TODO: check every now and then if floating ui addresses this
  const handleShiftTabClose = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (event.key === 'Tab' && event.shiftKey) {
        requestAnimationFrame(() => {
          const floatingEl = refs.floating.current;
          if (floatingEl && !floatingEl.contains(document.activeElement)) {
            if (controlledOpen === undefined) {
              setIsOpen(false);
            }
            onClose?.(undefined, { type: 'focusOut' });
          }
        });
      }
    },
    [controlledOpen, onClose, refs],
  );

  const menuClasses = classNames('ndl-menu', themeClassName, className);
  const ref = useMergeRefs([refs.setReference, item.ref, forwardedRef]);

  return (
    <FloatingNode id={nodeId}>
      {isRoot !== true && (
        <NestedMenuItem
          ref={ref}
          className={isNested ? 'MenuItem' : 'RootMenu'}
          isDisabled={isDisabled}
          style={style}
          htmlAttributes={{
            'data-focus-inside': hasFocusInside ? '' : undefined,
            'data-nested': isNested ? '' : undefined,
            'data-open': isOpen ? '' : undefined,
            role: isNested ? 'menuitem' : undefined,
            tabIndex: !isNested
              ? undefined
              : parent.activeIndex === item.index
                ? 0
                : -1,
            ...htmlAttributes,
            ...getReferenceProps(
              parent.getItemProps({
                onFocus(event: React.FocusEvent<HTMLButtonElement>) {
                  htmlAttributes?.onFocus?.(event);
                  setHasFocusInside(false);
                  parent.setHasFocusInside(true);
                },
              }),
            ),
          }}
          title={title}
          description={description}
          leadingVisual={icon}
        />
      )}
      <MenuContext.Provider
        value={{
          activeIndex,
          getItemProps,
          isOpen: isDisabled === true ? false : isOpen,
          setActiveIndex,
          setHasFocusInside,
        }}
      >
        <FloatingList elementsRef={elementsRef} labelsRef={labelsRef}>
          {isOpen && (
            <ConditionalWrap
              shouldWrap={isPortaled}
              wrap={(wrapChildren) => (
                <FloatingPortal root={portalTarget}>
                  {wrapChildren}
                </FloatingPortal>
              )}
            >
              <FloatingFocusManager
                context={context}
                modal={false}
                initialFocus={0}
                returnFocus={!isNested}
                closeOnFocusOut={true}
                guards={true}
              >
                <Component
                  ref={refs.setFloating}
                  className={menuClasses}
                  style={{
                    minWidth:
                      minWidth !== undefined ? `${minWidth}px` : undefined,
                    ...floatingStyles,
                    ...style,
                  }}
                  {...getFloatingProps({
                    onKeyDown: handleShiftTabClose,
                  })}
                >
                  {children}
                </Component>
              </FloatingFocusManager>
            </ConditionalWrap>
          )}
        </FloatingList>
      </MenuContext.Provider>
    </FloatingNode>
  );
};

/**
 * @internal This is used to build the menu item components. Do not export this component.
 */
const MenuItemComponent = <T extends React.ElementType = 'button'>({
  title,
  leadingContent,
  trailingContent,
  preLeadingContent,
  description,
  isDisabled,
  as,
  className,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: PolymorphicCommonProps<T, MenuItemComponentProps>) => {
  const classes = classNames(`ndl-menu-item`, className, {
    'ndl-disabled': isDisabled,
  });

  const Component = as ?? 'button';

  return (
    <Component
      className={classes}
      ref={ref}
      type="button"
      role="menuitem"
      disabled={isDisabled}
      style={style}
      {...restProps}
      {...htmlAttributes}
    >
      <div className="ndl-menu-item-inner">
        {Boolean(preLeadingContent) && (
          <div className="ndl-menu-item-pre-leading-content">
            {preLeadingContent}
          </div>
        )}
        {Boolean(leadingContent) && (
          <div className="ndl-menu-item-leading-content">{leadingContent}</div>
        )}

        <div className="ndl-menu-item-title-wrapper">
          <div className="ndl-menu-item-title">{title}</div>
          {Boolean(description) && (
            <div className="ndl-menu-item-description">{description}</div>
          )}
        </div>

        {Boolean(trailingContent) && (
          <div className="ndl-menu-item-trailing-content">
            {trailingContent}
          </div>
        )}
      </div>
    </Component>
  );
};

const MenuItem = <T extends React.ElementType = 'button'>({
  title,
  className,
  style,
  leadingVisual,
  trailingContent,
  description,
  isDisabled,
  as,
  onClick,
  onFocus,
  htmlAttributes,
  id,
  ref,
  ...restProps
}: PolymorphicCommonProps<T, MenuItemProps>) => {
  const menu = useContext(MenuContext);
  const itemLabel = typeof title === 'string' ? title : undefined;
  const item = useListItem({ label: isDisabled === true ? null : itemLabel });
  const tree = useFloatingTree();
  const isActive = item.index === menu.activeIndex;

  const mergedRefs = useMergeRefs([item.ref, ref]);

  return (
    <MenuItemComponent
      as={as ?? 'button'}
      style={style}
      className={className}
      ref={mergedRefs}
      title={title}
      description={description}
      leadingContent={leadingVisual}
      trailingContent={trailingContent}
      isDisabled={isDisabled}
      htmlAttributes={{
        ...htmlAttributes,
        tabIndex: isActive ? 0 : -1,
        ...menu.getItemProps({
          id: id,
          onClick(event: React.MouseEvent<HTMLButtonElement>) {
            onClick?.(event);
            tree?.events.emit('click', { id });
          },
          onFocus(event: React.FocusEvent<HTMLButtonElement>) {
            onFocus?.(event);
            menu.setHasFocusInside(true);
          },
        }),
      }}
      {...restProps}
    />
  );
};

const NestedMenuItem = <T extends React.ElementType = 'button'>({
  title,
  isDisabled,
  description,
  leadingVisual,
  as,
  onFocus,
  onClick,
  className,
  style,
  htmlAttributes,
  id,
  ref,
}: PolymorphicCommonProps<T, NestedMenuItemProps>) => {
  const menu = useContext(MenuContext);
  const itemLabel = typeof title === 'string' ? title : undefined;
  const item = useListItem({ label: isDisabled === true ? null : itemLabel });
  const isActive = item.index === menu.activeIndex;
  const mergedRefs = useMergeRefs([item.ref, ref]);

  return (
    <MenuItemComponent
      as={as ?? 'button'}
      style={style}
      className={className}
      ref={mergedRefs}
      title={title}
      description={description}
      leadingContent={leadingVisual}
      trailingContent={
        <ChevronRightIconOutline className="ndl-menu-item-chevron" />
      }
      isDisabled={isDisabled}
      htmlAttributes={{
        ...htmlAttributes,
        tabIndex: isActive ? 0 : -1,
        ...menu.getItemProps({
          onClick(event: React.MouseEvent<HTMLButtonElement>) {
            onClick?.(event);
          },
          onFocus(event: React.FocusEvent<HTMLButtonElement>) {
            onFocus?.(event);
            menu.setHasFocusInside(true);
          },
          onTouchStart() {
            menu.setHasFocusInside(true);
          },
        }),
        id: id,
      }}
    />
  );
};

const CategoryItem = <T extends React.ElementType = 'button'>({
  children,
  className,
  style,
  as,
  htmlAttributes,
  ref,
  ...restProps
}: PolymorphicCommonProps<T, CategoryItemProps>) => {
  const classes = classNames('ndl-menu-category-item', className);
  const Component: React.ElementType = as ?? 'div';
  return (
    <Component
      className={classes}
      style={style}
      ref={ref}
      {...restProps}
      {...htmlAttributes}
    >
      {children}
    </Component>
  );
};

const RadioItem = <T extends React.ElementType = 'button'>({
  title,
  leadingVisual,
  trailingContent,
  description,
  isDisabled,
  isChecked = false,
  onClick,
  onFocus,
  className,
  style,
  as,
  id,
  htmlAttributes,
  ref,
  ...restProps
}: PolymorphicCommonProps<T, RadioItemProps>) => {
  const menu = useContext(MenuContext);
  const itemLabel = typeof title === 'string' ? title : undefined;
  const item = useListItem({ label: isDisabled === true ? null : itemLabel });
  const tree = useFloatingTree();
  const isActive = item.index === menu.activeIndex;

  const mergedRefs = useMergeRefs([item.ref, ref]);
  const classes = classNames('ndl-menu-radio-item', className, {
    'ndl-checked': isChecked,
  });

  return (
    <MenuItemComponent
      as={as ?? 'button'}
      style={style}
      className={classes}
      ref={mergedRefs}
      title={title}
      description={description}
      preLeadingContent={
        isChecked ? (
          <CheckIconOutline className="n-size-5 n-shrink-0 n-self-center" />
        ) : null
      }
      leadingContent={leadingVisual}
      trailingContent={trailingContent}
      isDisabled={isDisabled}
      htmlAttributes={{
        ...htmlAttributes,
        'aria-checked': isChecked,
        role: 'menuitemradio',
        tabIndex: isActive ? 0 : -1,
        ...menu.getItemProps({
          id: id,
          onClick(event: React.MouseEvent<HTMLButtonElement>) {
            onClick?.(event);
            tree?.events.emit('click', { id });
          },
          onFocus(event: React.FocusEvent<HTMLButtonElement>) {
            onFocus?.(event);
            menu.setHasFocusInside(true);
          },
        }),
      }}
      {...restProps}
    />
  );
};

const MenuItems = <T extends React.ElementType = 'div'>({
  as,
  children,
  className,
  htmlAttributes,
  style,
  ref,
  ...restProps
}: PolymorphicCommonProps<T, MenuItemsProps>) => {
  const classes = classNames('ndl-menu-items', className);
  const Component: React.ElementType = as ?? 'div';
  return (
    <Component
      className={classes}
      style={style}
      ref={ref}
      {...restProps}
      {...htmlAttributes}
    >
      {children}
    </Component>
  );
};

const MenuGroup = ({
  children,
  className,
  htmlAttributes,
  style,
  ref,
  ...restProps
}: CommonProps<'div', MenuGroupProps>) => {
  const classes = classNames('ndl-menu-group', className);
  return (
    <div
      className={classes}
      style={style}
      ref={ref}
      role="group"
      {...restProps}
      {...htmlAttributes}
    >
      {children}
    </div>
  );
};

const Menu = Object.assign(MenuWrapper, {
  CategoryItem: CategoryItem,
  Divider: Divider,
  Group: MenuGroup,
  Item: MenuItem,
  /**
   * @deprecated Use Menu.Group instead if you want to group items together. If not, you can just omit this component in your implementation.
   */
  Items: MenuItems,
  RadioItem: RadioItem,
});
export { Menu };
